import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Briefcase, Users, ShoppingCart, ArrowLeftRight, ArrowRight, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const OnboardingStep1 = () => {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSelect = (type) => {
    setSelected(type);
  };

  const handleNext = async () => {
    if (!selected) {
      toast.error('Please select your role');
      return;
    }

    setLoading(true);
    try {
      await api.post('/users/update-type', { user_type: selected });
      navigate('/admin/onboarding/step2');
    } catch (error) {
      toast.error('Failed to save selection');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    {
      id: 'buyer',
      icon: ShoppingCart,
      title: 'I am a Buyer',
      description: 'I purchase commodities directly for my business or organization. I\'m the end buyer looking to source quality products from Brazilian suppliers.'
    },
    {
      id: 'seller',
      icon: Users,
      title: 'I am a Seller',
      description: 'I sell commodities directly to buyers. I represent producers, exporters, or trading companies with products available for sale.'
    },
    {
      id: 'buyer_seller',
      icon: ArrowLeftRight,
      title: 'I am a Buyer & Seller',
      description: 'I both purchase and sell commodities. I operate as a trader who sources products and resells them to other buyers in the market.'
    },
    {
      id: 'broker',
      icon: Briefcase,
      title: 'I am a Broker',
      description: 'I facilitate commodity transactions between buyers and sellers. I act as an intermediary, connecting parties and earning commission on successful trades.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 p-10"
        >
          {/* Progress indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-900 text-white flex items-center justify-center font-bold">1</div>
              <div className="w-24 h-1 bg-slate-200"></div>
              <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center font-bold">2</div>
              <div className="w-24 h-1 bg-slate-200"></div>
              <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center font-bold">3</div>
            </div>
          </div>

          <h1 className="text-4xl font-bold text-slate-800 text-center mb-4">Step 1: Identify Your Role</h1>
          
          {/* Important notice */}
          <div className="bg-amber-50 border-l-4 border-amber-600 p-6 mb-8 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-slate-800 mb-2">Important: Accurate Information Required</h3>
                <p className="text-slate-700 mb-3">
                  It is <strong>critical</strong> that you identify yourself properly. False statements provided during registration could be used against you in case of disputes or legal challenges.
                </p>
                <p className="text-slate-700">
                  <strong>Trados welcomes Buyers, Sellers, and Brokers.</strong> Please select the option that best describes your role in the commodity trade.
                </p>
              </div>
            </div>
          </div>

          {/* Role selection boxes - 2x2 grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {roleOptions.map((role) => {
              const IconComponent = role.icon;
              const isSelected = selected === role.id;
              
              return (
                <motion.div
                  key={role.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelect(role.id)}
                  data-testid={`${role.id}-option`}
                  className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all ${
                    isSelected
                      ? 'border-emerald-600 bg-emerald-50 shadow-lg'
                      : 'border-slate-200 bg-white hover:border-emerald-300 hover:shadow-md'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-emerald-600' : 'bg-slate-100'
                    }`}>
                      <IconComponent className={`w-7 h-7 ${isSelected ? 'text-white' : 'text-slate-600'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-800 mb-2">{role.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {role.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Next button */}
          <button
            onClick={handleNext}
            disabled={!selected || loading}
            data-testid="step1-next-button"
            className="w-full bg-emerald-900 hover:bg-emerald-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-4 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 group"
          >
            <span>{loading ? 'Saving...' : 'Continue to Next Step'}</span>
            {!loading && <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingStep1;
